import { useState, useEffect, useCallback } from 'react';
import { getFieldConfigs } from '../lib/api/fieldConfig';

/**
 * 필드 설정을 관리하는 커스텀 훅
 * 데이터가 없으면 모든 필드를 기본 표시 (기본값 = 사용)
 * 
 * @param {string} moduleCd - 모듈 코드 (예: 'CHANCE', 'PROJECT')
 * @returns {object} { fieldConfigs, isFieldEnabled, isFieldRequired, loading, error }
 */
const useFieldConfig = (moduleCd) => {
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFieldConfigs = async () => {
      if (!moduleCd) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getFieldConfigs(moduleCd);
        setFieldConfigs(response.data.list || []);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch field configs:', err);
        setError(err);
        // 에러 시에도 빈 배열로 설정 (모든 필드 표시)
        setFieldConfigs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFieldConfigs();
  }, [moduleCd]);

  /**
   * 특정 필드가 사용 가능한지 확인
   * 설정이 없으면 기본 true (표시)
   * @param {string} fieldCd - 필드 코드
   * @returns {boolean} 사용 여부
   */
  const isFieldEnabled = useCallback((fieldCd) => {
    // 설정 데이터가 없으면 기본 표시
    if (!fieldConfigs || fieldConfigs.length === 0) return true;
    
    const config = fieldConfigs.find(c => c.fieldCd === fieldCd);
    // 해당 필드 설정이 없으면 기본 표시, 있으면 useYn 확인
    return config ? config.useYn === 'Y' : true;
  }, [fieldConfigs]);

  /**
   * 특정 필드가 필수인지 확인
   * 설정이 없으면 기본 false (선택)
   * @param {string} fieldCd - 필드 코드
   * @returns {boolean} 필수 여부
   */
  const isFieldRequired = useCallback((fieldCd) => {
    // 설정 데이터가 없으면 기본 선택
    if (!fieldConfigs || fieldConfigs.length === 0) return false;
    
    const config = fieldConfigs.find(c => c.fieldCd === fieldCd);
    // 해당 필드 설정이 없으면 기본 선택, 있으면 requiredYn 확인
    return config ? config.requiredYn === 'Y' : false;
  }, [fieldConfigs]);

  /**
   * 필드 설정 객체 반환
   * @param {string} fieldCd - 필드 코드
   * @returns {object|null} 필드 설정 또는 null
   */
  const getFieldConfig = useCallback((fieldCd) => {
    if (!fieldConfigs || fieldConfigs.length === 0) return null;
    return fieldConfigs.find(c => c.fieldCd === fieldCd) || null;
  }, [fieldConfigs]);

  return {
    fieldConfigs,
    isFieldEnabled,
    isFieldRequired,
    getFieldConfig,
    loading,
    error,
  };
};

export default useFieldConfig;
